import { Box, Typography, Button, styled, Card, CardContent } from '@mui/material'
import SaleFlag from '../../atoms/SaleFlag/SaleFlag'

export const StyledCard = styled(Card, {
  shouldForwardProp: prop => prop !== 'isCurrentItem',
})(({ theme, isCurrentItem }) => ({
  overflow: 'visible',
  width: '100%',
  position: 'relative',
  height: '100%',
  border: isCurrentItem ? '2px solid rgba(0, 83, 160, 0.12)' : 'none',
  borderRadius: '2px',
  paddingRight: '0',
  padding: isCurrentItem ? '5px 3px' : '7px 5px',
  boxSizing: 'border-box',
  '& .MuiCardActions-root:last-of-type': {
    margin: '8px 8px 0 0',
    WebkitTapHighlightColor: 'transparent',
  },
}))

export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  justifyContent: 'space-between',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: '0 !important',
}))

export const StyledProductTileButton = styled(Button)`
  font-size: 1rem;
`

export const ProductTileButtonWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: '30px 35px 15px 35px',
  [theme.breakpoints.down('md')]: {
    padding: '20px 10px 10px',
    button: {
      fontSize: '14px',
    },
  },
}))

export const StyledProductAttributes = styled('table')(({ theme }) => ({
  marginBottom: '0',
  tr: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '45px',
    fontSize: '16px',
    padding: '5px 10px',
    '& td': {
      padding: 0,
    },
    '& td > p': {
      color: theme.palette.primary.dark,
      marginBottom: '0',
      fontWeight: '500',
      '& > span:not(.stock-message)': {
        color: '#333333',
        textTransform: 'capitalize',
      },
    },
    '&:first-of-type': {
      height: '64px',
      '& td > p': {
        height: '45px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: '2',
      },
    },
    '&:last-of-type': {
      height: '69px',
    },
    '&:nth-of-type(odd):hover': {
      backgroundColor: 'inherit',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(0,83,160, 0.04)',
      '&:hover': {
        backgroundColor: 'rgba(0,83,160, 0.04)',
      },
    },
    '.stock-message': {
      fontWeight: '500',
    },
    [theme.breakpoints.down('md')]: {
      padding: '0 5px',
      '& td > span, & td > p, & td > p > span, .stock-message': {
        fontSize: '12px',
      },
    },
  },
}))

export const AvailableColorsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& > .MuiBox-root': {
    margin: 0,
  },
}))

export const ExtraAvailableColorsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '19px',
  border: '1px solid',
  borderColor: theme.palette.secondary.main,
  marginLeft: '3px',
  [theme.breakpoints.down('md')]: {
    width: '24px',
    height: '24px',
  },
}))

export const StyledPriceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& > .MuiStack-root': {
    padding: 0,
  },
  '& > p': {
    fontSize: '14px',
    textTransform: 'capitalize',
    [theme.breakpoints.down('md')]: {
      fontSize: '10px !important',
    },
  },
}))

export const StyledReviewsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '.MuiRating-root': {
    width: 'auto',
    '.MuiRating-decimal': {
      fontSize: '18px',
      '& > span:not(:last-of-type)': {
        zIndex: '1 !important',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '14px !important',
      },
    },
  },
  '& > p:first-of-type': {
    fontSize: '12px',
    fontWeight: '400',
    textDecoration: 'underline',
    textTransform: 'capitalize',
    [theme.breakpoints.down('md')]: {
      fontSize: '10px',
    },
  },
}))

export const StyledSaleFlag = styled(SaleFlag, {
  shouldForwardProp: prop => prop !== 'isCurrentItem',
})(({ theme, isCurrentItem }) => ({
  margin: isCurrentItem ? '2px 0 0 0' : '4px 0 0 2px',
  borderRadius: '4px',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 8px',
    '& .MuiTypography-root': {
      fontSize: '12px !important',
      lineHeight: '16px !important',
    },
  },
}))

export const StyledAvailableColorsTypography = styled(Typography)(({ theme }) => ({
  fontSize: '16px !important',
  fontWeight: '600',
  color: theme.palette.secondary.lighter,
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px !important',
  },
}))

export const StyledAvailabilityTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.main,
  marginLeft: '0px!important',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px !important',
  },
  '& p': {
    lineHeight: '12px',
  },
}))

export const TitleAndFlagWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
  },
}))

export const StyledTitle = styled('div')(({ theme, isCurrentItem }) => ({
  fontWeight: isCurrentItem ? 600 : 500,
  marginLeft: '0 !important',
  lineHeight: 'normal',

  // ellipses with two lines
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  lineClamp: '2',
  WebkitBoxOrient: 'vertical',
  '&:hover': {
    color: '#333',
  },

  [theme.breakpoints.down('md')]: {
    fontSize: '14px !important',
    flexBasis: '100%',
  },
}))

export const CurrentItemFlagWrapper = styled('div')(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: 'rgba(0, 83, 160, 0.12)',
  padding: '2px 4px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 600,
  marginRight: '5px',
  lineHeight: 'normal',
  color: `${theme.palette.primary.dark} !important`,
  marginTop: 0,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    margin: 0,
    marginLeft: '-1px',
    padding: '3px 2px',
    fontSize: '8px',
    lineHeight: '8px',
    borderRadius: '2px',
  },
}))
